import { useState, useMemo } from "react";
import {
  useMutation,
  useInfiniteQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { WorkflowService } from "@/generated";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { SpotToast } from "@/components/SpotToast";
import BaseDialog from "../Modals/BaseDialog";
import {
  WorkflowVersionContext,
  WorkflowVersionInput,
} from "../Form/WorkflowVersionField";
import { ExportPreviewDialog } from "./ExportPreviewDialog";
import { useRouter } from "next/navigation";

const MAX_VERSIONS_PAGE = 10;

interface WorkflowVersionActionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string;
  workflowName: string;
  action: "duplicate" | "export";
}

export const WorkflowVersionActionDialog = ({
  isOpen,
  onOpenChange,
  workflowId,
  workflowName,
  action,
}: WorkflowVersionActionDialogProps) => {
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    "current"
  );

  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const router = useRouter();

  // All hooks must be called unconditionally (before any returns)
  const { data } = useInfiniteQuery({
    queryKey: [DATA_KEYS.WORKFLOW_VERSIONS, workflowId, spotGroup.id],
    queryFn: ({ pageParam = 1 }) =>
      WorkflowService.getWorkflowVersions({
        path: {
          group_id: spotGroup.id,
          workflow_id: workflowId,
        },
        query: {
          page: pageParam,
          size: MAX_VERSIONS_PAGE,
        },
      }),
    enabled: isOpen && !!workflowId && action === "duplicate",
    placeholderData: keepPreviousData,
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.data.page) return;
      return firstPage.data.page > 1 ? firstPage.data.page - 1 : undefined;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.data.page || !lastPage.data.pages) return;

      return lastPage.data.page < lastPage.data.pages
        ? allPages.length + 1
        : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const versions = useMemo(
    () => data?.pages?.flatMap((page) => page.data.items),
    [data?.pages]
  );

  const duplicateWfMutation = useMutation({
    mutationFn: WorkflowService.duplicateWorkflow,
    onSuccess: ({ data: { id } }) => {
      SpotToast.success("Workflow duplicated");
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS],
      });
      router.push(`/flows/${id}`);
      onOpenChange(false);
    },
    onError: () => {
      SpotToast.error("Error duplicating the flow");
    },
  });

  const handleAction = () => {
    const versionId =
      selectedVersionId === "current" ? undefined : selectedVersionId;

    const baseParams = {
      path: {
        group_id: spotGroup.id,
        workflow_id: workflowId,
      },
    };

    const params = versionId
      ? { ...baseParams, query: { version_id: versionId } }
      : baseParams;

    duplicateWfMutation.mutate(params);
  };

  const isLoading_ = duplicateWfMutation.status === "pending";

  // For export, use the new ExportPreviewDialog
  if (action === "export") {
    return (
      <ExportPreviewDialog
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        entityId={workflowId}
        entityName={workflowName}
        entityType="workflow"
      />
    );
  }

  // For duplicate, keep the original behavior
  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Duplicate flow"
      description={`Select which version of ${workflowName} you want to duplicate.`}
      footerActions={[
        {
          label: "Cancel",
          props: {
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isLoading_,
          },
        },
        {
          label: isLoading_ ? "Processing..." : "Duplicate",
          props: {
            onClick: handleAction,
            disabled: isLoading_,
          },
        },
      ]}
    >
      <div className="space-y-4">
        <WorkflowVersionContext.Provider value={workflowId}>
          <WorkflowVersionInput
            value={selectedVersionId || undefined}
            onChange={setSelectedVersionId}
            allowCurrentVersion
            disableDeselection
            disablePortal
          />
        </WorkflowVersionContext.Provider>
      </div>
    </BaseDialog>
  );
};
